import { useMemo, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { capacityMatrix, capacityMatrixProposed, months, personProjectsInMonth, personSummary, pendingApprovalsCount } from "../lib/calc";
import { leaveByPersonMonth, bandWithLeave } from "../lib/leave";
import { fmtPctCell, fmtPct, fmtMonthShort } from "../lib/format";
import { HOURS_PER_FTE_WEEK } from "../lib/constants";
import InfoTip from "../components/InfoTip";
import { ApprovalsQueue } from "../components/ApprovalsQueue";
import { AlertTriangle, Plane } from "lucide-react";
import DragScrollContainer from "../components/DragScrollContainer";
import RemovePersonButton from "../components/RemovePersonButton";
import PageHeader from "../components/PageHeader";

export default function Capacity() {
  const { data, rawData } = useData();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const m = months(data);
  const matrix = useMemo(() => capacityMatrix(data), [data]);
  const proposedExtra = useMemo(() => capacityMatrixProposed(rawData), [rawData]);
  const pendingCount = useMemo(() => (isAdmin ? pendingApprovalsCount(rawData) : 0), [rawData, isAdmin]);
  const leaveFrac = useMemo(() => leaveByPersonMonth(rawData), [rawData]);
  // AVG includes leave, exactly as the cells do (leave is additive).
  const summary = useMemo(() => personSummary(data, leaveFrac), [data, leaveFrac]);
  const [selected, setSelected] = useState(null); // { personId, month }
  const [unit, setUnit] = useState("pct"); // pct | fte | hours
  const [showProposed, setShowProposed] = useState(isAdmin); // principals never see proposed of others
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  // Personnel is its own top-level page again (was ?tab=staff before).
  // Keep the old URL working — anything that still points here forwards.
  if (tabParam === "staff") return <Navigate to="/admin/personnel" replace />;
  // Approvals tab is admin-only; principals viewing capacity get read-only
  // matrix only.
  const tab = (tabParam === "approvals" && isAdmin) ? "approvals" : "matrix";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    if (t === "matrix") next.delete("tab"); else next.set("tab", t);
    setSearchParams(next, { replace: true });
  };
  const hoursPerFte = data?.meta?.hoursPerFte ?? HOURS_PER_FTE_WEEK;
  const fmtCell = (load) => {
    if (unit === "pct")   return load > 0 ? `${Math.round(load * 100)}%` : "";
    if (unit === "fte")   return load > 0 ? load.toFixed(2) : "";
    return load > 0 ? `${Math.round(load * hoursPerFte)}h` : "";
  };

  // Insights
  const mostOver = [...summary].sort((a,b) => b.over - a.over || b.peak - a.peak).slice(0,3);
  const mostSpare = [...summary].sort((a,b) => b.spareTotal - a.spareTotal).slice(0,3);

  // Month with most over-allocation — leave counts, as it does in the cells.
  const monthOver = m.map((mo) => {
    const overPeople = data.people.filter(
      (p) => (matrix[p.id][mo] || 0) + (leaveFrac[p.id]?.[mo] || 0) > 1.0001
    ).length;
    return { month: mo, count: overPeople };
  }).sort((a,b) => b.count - a.count);

  return (
    <div className="space-y-8" data-testid="capacity-view">
      <PageHeader
        section="PEOPLE"
        title={tab === "matrix" ? "Capacity matrix" : "Approvals"}
        subtitle={
          tab === "matrix"
            ? "People × months. Each cell is one person's planned load for one month across every project they're on."
            : "Sign off principals' proposed allocations. Approved allocations flow into the capacity matrix."
        }
      />
      {/* Section tabs — Approvals hidden for principals (admin-only). */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="tab-strip" data-testid="capacity-tabs">
          {[
            { id: "matrix",    label: "Capacity matrix", show: true },
            { id: "approvals", label: `Approvals${pendingCount ? ` · ${pendingCount}` : ""}`, show: isAdmin },
          ].filter((t) => t.show).map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              aria-pressed={tab === t.id}
              className={tab === t.id ? "active" : ""}
              data-testid={`capacity-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "matrix" && isAdmin && (
          <label className="inline-flex items-center gap-2 border border-[var(--rule)] px-3 py-1.5 cursor-pointer" title="Adds pending proposals into the cells (outlined split) — the approved-only number stays visible. Independent of the Confirmed/Forecast scenario toggle.">
            <input
              type="checkbox"
              checked={showProposed}
              onChange={(e) => setShowProposed(e.target.checked)}
              data-testid="include-proposed-toggle"
            />
            <span className="text-[11px] font-display tracked-tight uppercase text-[var(--muted-fg)]">
              Include proposed{pendingCount ? ` (${pendingCount})` : ""}
            </span>
          </label>
        )}
      </div>

      {tab === "approvals" ? (
        <ApprovalsQueue />
      ) : (
      <>
      <div className="flex justify-end items-end flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="inline-flex border border-[var(--rule-strong)] items-center" data-testid="unit-toggle">
            {[
              { id: "pct", label: "%" },
              { id: "fte", label: "FTE" },
              { id: "hours", label: "Hrs/wk" },
            ].map((u) => (
              <button
                key={u.id}
                onClick={() => setUnit(u.id)}
                className={`px-3 py-1.5 text-[11px] font-display tracked-tight uppercase ${unit === u.id ? "bg-[var(--ink)] text-white" : "text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]"}`}
                data-testid={`unit-${u.id}`}
              >{u.label}</button>
            ))}
            <span className="pl-2 pr-2">
              <InfoTip side="bottom" label="Units explained">
                <strong>%</strong> — share of full-time (40% = 16 hrs/wk).<br/>
                <strong>FTE</strong> — Full-Time Equivalent. 1.00 = one full person.<br/>
                <strong>Hrs/wk</strong> — weekly hours, assuming 40 hrs = 1 FTE.
              </InfoTip>
            </span>
          </div>
          <Legend />
        </div>
      </div>

      {/* Matrix */}
      <DragScrollContainer className="border border-[var(--rule)] overflow-x-auto thin-scroll" data-testid="capacity-matrix">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left px-4 py-3 eyebrow border-b border-[var(--rule-strong)] sticky left-0 bg-white z-10">Person</th>
              {m.map((mo) => (
                <th key={mo} className="text-center px-2 py-3 eyebrow border-b border-[var(--rule-strong)] min-w-[68px]">{fmtMonthShort(mo)}</th>
              ))}
              <th
                className="text-right px-3 py-3 eyebrow border-b border-[var(--rule-strong)] border-l border-[var(--rule)]"
                title="Average of the % cells across the months in view — leave included, the same way each cell counts it."
                data-testid="capacity-avg-header"
              >
                Avg
              </th>
            </tr>
          </thead>
          <tbody>
            {data.people.map((person) => (
              <tr key={person.id} className="border-t border-[var(--rule)]" data-testid={`capacity-row-${person.id}`}>
                <td className="px-4 py-2 sticky left-0 bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[var(--ink)]">{person.name}</div>
                      <div className="eyebrow mt-0.5">FTE · <span className="font-mono-num text-[var(--soft-fg)]">{person.fullTimeCapacity.toFixed(2)}</span></div>
                    </div>
                    <RemovePersonButton person={person} />
                  </div>
                </td>
                {m.map((mo) => {
                  const load = matrix[person.id][mo] || 0;
                  const lv = leaveFrac[person.id]?.[mo] || 0;
                  const extra = showProposed ? (proposedExtra[person.id]?.[mo] || 0) : 0;
                  const band = bandWithLeave(load, lv);
                  const isSel = selected?.personId === person.id && selected?.month === mo;
                  const isOver = band === "over";
                  // Utilisation is ADDITIVE — leave stacks on top of project
                  // load. 80% booked + 25% leave reads as 105%. FTE and hour
                  // units are absolute and unaffected.
                  const total = load + lv;
                  const displayLoad = unit === "pct" ? total : load;
                  return (
                    <td key={mo} className="p-1">
                      <button
                        onClick={() => setSelected({ personId: person.id, month: mo })}
                        className={`relative w-full h-12 font-mono-num text-xs cell-${band} ${isSel ? "outline outline-2 outline-[var(--ink)]" : ""}`}
                        data-testid={`cell-${person.id}-${mo}`}
                        title={`${person.name} · ${mo} · ${fmtPct(load,0)} booked${lv > 0 ? ` · +${fmtPct(lv,0)} leave → ${fmtPct(total,0)} total` : " approved"}${extra ? ` · ${extra > 0 ? "+" : "−"}${Math.round(Math.abs(extra)*100)}% proposed (awaiting approval)` : ""}${isOver ? " · over capacity" : ""}`}
                      >
                        {isOver && (
                          <AlertTriangle
                            size={10}
                            className="absolute top-1 right-1 text-[#6B2F2A]"
                            data-testid={`warn-${person.id}-${mo}`}
                          />
                        )}
                        {lv > 0 && (
                          <Plane
                            size={9}
                            className="absolute bottom-1 left-1 text-[var(--muted-fg)]"
                            data-testid={`leave-flag-${person.id}-${mo}`}
                          />
                        )}
                        {fmtCell(displayLoad)}
                        {extra !== 0 && (
                          <span
                            className="block mx-auto mt-0.5 px-1 text-[9px] leading-tight font-mono-num text-[#4a5a7a] border border-dashed border-[#4a5a7a]/60 w-fit"
                            data-testid={`proposed-extra-${person.id}-${mo}`}
                          >
                            {extra > 0 ? "+" : "−"}{Math.round(Math.abs(extra) * 100)}%
                          </span>
                        )}
                      </button>
                    </td>
                  );
                })}
                <td
                  className="px-3 py-2 text-right font-mono-num border-l border-[var(--rule)]"
                  data-testid={`capacity-avg-${person.id}`}
                  title={`Average of ${m.length} month${m.length === 1 ? "" : "s"} in view · booked load + leave`}
                >
                  {fmtPct(summary.find((s)=>s.id===person.id)?.avg ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DragScrollContainer>

      {/* Detail + insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[var(--rule)] p-5 min-h-[180px]" data-testid="capacity-detail-panel">
          <div className="eyebrow">Detail</div>
          {selected && data.people.some((p) => p.id === selected.personId) ? (
            <Detail data={data} selected={selected} matrix={matrix} leaveFrac={leaveFrac} />
          ) : (
            <div className="font-mono-num text-sm text-[var(--soft-fg)] mt-3">
              Tap a cell to see which projects are eating that person’s time that month.
            </div>
          )}
        </div>

        <div className="space-y-4">
          <InsightCard title="Most over-allocated" testid="insight-over"
                       info="People with the most month-by-month overload (>100%), counting annual leave on top of booked load exactly as the cells do. Often signals the next hire — or a project that should be re-staffed.">
            {mostOver.map((p) => p.over > 0 && (
              <div key={p.id} className="flex justify-between py-1 border-b border-[var(--rule)] text-sm">
                <span>{p.name}</span>
                <span className="font-mono-num text-[#6B2F2A]">{p.over} mo · peak {fmtPct(p.peak)}</span>
              </div>
            ))}
            {mostOver.every((p)=>p.over===0) && <div className="text-sm font-mono-num text-[var(--soft-fg)]">No over-allocation in plan.</div>}
          </InsightCard>

          <InsightCard title="Most spare capacity" testid="insight-spare"
                       info="People with the most unused capacity across the period — booked load plus leave subtracted from full time, so time away is never counted as free. Candidates for redeployment onto over-allocated work.">
            {mostSpare.map((p) => (
              <div key={p.id} className="flex justify-between py-1 border-b border-[var(--rule)] text-sm">
                <span>{p.name}</span>
                <span className="font-mono-num text-[#2C4A38]">{p.spareTotal.toFixed(1)} FTE-mo free</span>
              </div>
            ))}
          </InsightCard>

          <InsightCard title="Tightest months" testid="insight-tight"
                       info="The months where the most people are forecast over 100%, leave included. Useful when deciding which months to push deadlines off.">
            {monthOver.filter((x)=>x.count>0).slice(0,3).map((x) => (
              <div key={x.month} className="flex justify-between py-1 border-b border-[var(--rule)] text-sm">
                <span>{x.month}</span>
                <span className="font-mono-num text-[#6B2F2A]">{x.count} over</span>
              </div>
            ))}
            {monthOver.every((x)=>x.count===0) && <div className="text-sm font-mono-num text-[var(--soft-fg)]">No overload months.</div>}
          </InsightCard>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

function Detail({ data, selected, matrix, leaveFrac }) {
  const person = data.people.find((p) => p.id === selected.personId);
  const load = matrix[selected.personId][selected.month] || 0;
  const lv = leaveFrac?.[selected.personId]?.[selected.month] || 0;
  const rows = personProjectsInMonth(data, selected.personId, selected.month);
  const band = bandWithLeave(load, lv);
  return (
    <div className="mt-2">
      <div className="flex items-baseline gap-4 flex-wrap">
        <h3 className="font-display text-xl">{person?.name}</h3>
        <span className="eyebrow">{selected.month}</span>
        <span className={`chip ${band==="over" ? "chip-no" : band==="available" ? "chip-yes" : "chip-tentative"}`}>
          {fmtPct(load + lv)} total {band === "over" ? "· overloaded" : band === "empty" ? "· free" : ""}
        </span>
        {lv > 0 && (
          <span className="chip chip-tentative inline-flex items-center gap-1" data-testid="detail-leave-chip">
            <Plane size={10} /> +{Math.round(lv * 100)}% leave · {fmtPct(load)} booked = {fmtPct(load + lv)}
          </span>
        )}
      </div>
      <table className="w-full text-sm mt-4">
        <thead>
          <tr className="eyebrow">
            <th className="text-left py-2 border-b border-[var(--rule)]">Project</th>
            <th className="text-right py-2 border-b border-[var(--rule)]">Load</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.projectId} className="border-b border-[var(--rule)]">
              <td className="py-2">{r.projectName}</td>
              <td className="py-2 text-right font-mono-num">{fmtPct(r.load)}</td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={2} className="py-3 text-center font-mono-num text-sm text-[var(--soft-fg)]">No allocations this month.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function InsightCard({ title, children, testid, info }) {
  return (
    <div className="border border-[var(--rule)] p-5" data-testid={testid}>
      <div className="eyebrow mb-3 inline-flex items-center">
        {title}
        {info && <InfoTip side="bottom" label={title}>{info}</InfoTip>}
      </div>
      {children}
    </div>
  );
}

function Legend() {
  return (
    <div className="flex items-center gap-3 text-xs font-mono-num flex-wrap">
      <LegendDot cls="cell-empty"     label="Empty" />
      <LegendDot cls="cell-available" label="Available · <80%" />
      <LegendDot cls="cell-balanced"  label="Balanced · 80–100%" />
      <LegendDot cls="cell-over"      label="Over · >100%" />
      <span className="inline-flex items-center gap-1.5 text-[var(--muted-fg)]" title="Leave adds to project load — a full week (5 workdays / ~20 in a month) reads as +25% on top of whatever's booked.">
        <Plane size={11} /> on leave · adds to load
      </span>
    </div>
  );
}
function LegendDot({ cls, label }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`inline-block w-4 h-4 ${cls} border border-[var(--rule)]`} />
      <span className="text-[var(--muted-fg)]">{label}</span>
    </span>
  );
}
